import React, { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { differentiators, stats } from '../mockData';
import { Award, Users, Target, Zap } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export default function DifferentiatorsSection() {
  const container = useRef();
  const headerRef = useRef();
  const statsRef = useRef();
  const diffsRef = useRef([]);

  useGSAP(() => {
    gsap.from(headerRef.current, {
      opacity: 0,
      y: 40,
      duration: 0.8,
      scrollTrigger: {
        trigger: container.current,
        start: "top 70%",
        end: "top 30%",
        scrub: 0.5,
      }
    });

    gsap.from(statsRef.current.children, {
      opacity: 0,
      scale: 0.8,
      duration: 0.6,
      stagger: 0.1,
      scrollTrigger: {
        trigger: statsRef.current,
        start: "top 80%",
        end: "top 35%",
        scrub: 0.5,
      }
    });

    diffsRef.current.forEach((diff, index) => {
      if (diff) {
        gsap.from(diff, {
          opacity: 0,
          y: 50,
          duration: 0.7,
          scrollTrigger: {
            trigger: diff,
            start: "top 85%",
            end: "top 45%",
            scrub: 0.5,
          }
        });
      }
    });
  }, { scope: container });

  const icons = [Award, Users, Target, Zap];

  return (
    <section id="differentiators" ref={container} className="section section-light">
      <div className="section-content">
        <div ref={headerRef} className="section-header">
          <div className="section-eyebrow">Why IronQuill</div>
          <h2 className="section-title">What Sets Us Apart</h2>
          <p className="section-description">
            Not just proposal writers—strategic partners who understand government contracting, evaluation criteria, and what it takes to win.
          </p>
        </div>

        {/* Stats Grid */}
        <div ref={statsRef} className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
              <div className="stat-description">{stat.description}</div>
            </div>
          ))}
        </div>

        {/* Differentiators Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px', marginTop: '80px' }}>
          {differentiators.map((diff, index) => {
            const IconComponent = icons[index];
            return (
              <div 
                key={diff.id}
                ref={(el) => diffsRef.current[index] = el}
                style={{
                  background: 'var(--bg-overlay)',
                  border: '1px solid var(--border-subtle)',
                  padding: '36px',
                  transition: 'all 0.4s ease'
                }}
              >
                <div style={{ color: 'var(--gold-primary)', marginBottom: '20px' }}>
                  <IconComponent size={36} />
                </div>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>
                  {diff.title}
                </h3>
                <p style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '16px' }}>
                  {diff.description}
                </p>
                <div style={{ 
                  fontSize: '14px', 
                  color: 'var(--gold-primary)', 
                  fontWeight: '500', 
                  paddingTop: '16px', 
                  borderTop: '1px solid var(--border-subtle)' 
                }}>
                  Impact: {diff.impact}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
